/**
 * Generated by `pnpm generate:sdk-types` — DO NOT EDIT BY HAND.
 *
 * `PluginPermission` union + `VALID_PLUGIN_PERMISSIONS` const,
 * sourced from `@fias/db-types`. Generated (not re-exported) so the
 * SDK has no runtime dependency on db-types — the SDK ships in
 * plugin iframes and must stay bundle-clean.
 *
 * `tests/architecture/sdk-permissions-match-db-types.test.ts` ensures
 * this file stays in lockstep with the canonical source.
 */

export type PluginPermission =
  | 'ai:actions'
  | 'assets:community:publish'
  | 'assets:community:read'
  | 'assets:read'
  | 'connections:use'
  | 'data:agents'
  | 'data:search'
  | 'data:store'
  | 'data:workspace'
  | 'docsign:envelopes:read'
  | 'email:company:send'
  | 'entities:audio_generate'
  | 'entities:client_invoke'
  | 'entities:image_edit'
  | 'entities:image_generate'
  | 'entities:invoke'
  | 'entities:web_search'
  | 'litcraft:hub'
  | 'navigation:open_arche'
  | 'sandbox:vendored-libraries'
  | 'schedule:invoke'
  | 'storage:local'
  | 'storage:sandbox'
  | 'store:purchase'
  | 'theme:read'
  | 'user:profile:read'
  | 'vault:arche-saves:write'
  | 'vault:documents:read'
  | 'vault:documents:write'
  | 'vault:user-documents:edit'
  | 'vault:user-documents:read'
  | 'vault:user-documents:write'
  | 'vault:workspace-documents:read';

export const VALID_PLUGIN_PERMISSIONS: readonly PluginPermission[] = [
  'ai:actions',
  'assets:community:publish',
  'assets:community:read',
  'assets:read',
  'connections:use',
  'data:agents',
  'data:search',
  'data:store',
  'data:workspace',
  'docsign:envelopes:read',
  'email:company:send',
  'entities:audio_generate',
  'entities:client_invoke',
  'entities:image_edit',
  'entities:image_generate',
  'entities:invoke',
  'entities:web_search',
  'litcraft:hub',
  'navigation:open_arche',
  'sandbox:vendored-libraries',
  'schedule:invoke',
  'storage:local',
  'storage:sandbox',
  'store:purchase',
  'theme:read',
  'user:profile:read',
  'vault:arche-saves:write',
  'vault:documents:read',
  'vault:documents:write',
  'vault:user-documents:edit',
  'vault:user-documents:read',
  'vault:user-documents:write',
  'vault:workspace-documents:read',
] as const;

export function isValidPluginPermission(perm: string): perm is PluginPermission {
  return (VALID_PLUGIN_PERMISSIONS as readonly string[]).includes(perm);
}

/**
 * USER-FACING one-line description per permission — what consent /
 * store surfaces show a non-technical user. Sourced from db-types'
 * PLUGIN_PERMISSION_DESCRIPTIONS.
 */
export const PLUGIN_PERMISSION_DESCRIPTIONS: Readonly<Record<PluginPermission, string>> = {
  'ai:actions':
    'Let Fias AI operate this arche for you — only actions you could do yourself in its interface, and risky ones ask you first.',
  'assets:community:publish':
    'Share images you made here so other people using this arche can see them, and manage what you have shared.',
  'assets:community:read':
    'Show you images that other people using this arche have shared. Everything shared is checked before anyone can see it.',
  'assets:read': "Show images and play sounds from this arche's published asset library.",
  'connections:use':
    'Use outside accounts (like X or LinkedIn) that you connect and let it use — you choose each account and each workspace, approve what it posts, and can take access away any time in Workspace Controls.',
  'data:agents':
    "Let Fias AI, and assistants you connect with an API key and allow here, read this arche's data — and change it where it says so — even while it is closed.",
  'data:search': "Search this arche's own data semantically — costs credits per search.",
  'data:store': "Save structured data in this arche's own private database.",
  'data:workspace': 'Create team workspaces and manage their members.',
  'docsign:envelopes:read':
    'Check on signature requests it started for you — who has signed, and what is still outstanding.',
  'email:company:send':
    'Send email as your Company from its own address (e.g. team@yourcompany.com) — only after a Company admin allows this arche to, and only to the people they choose. Each email is charged to the person using the arche.',
  'entities:audio_generate': 'Generate audio with platform AI — costs credits.',
  'entities:client_invoke':
    'Use free on-device tools (grammar check, translation) in your browser.',
  'entities:image_edit': 'Edit images with platform AI — costs credits.',
  'entities:image_generate': 'Generate images with platform AI — costs credits.',
  'entities:invoke': 'Use platform AI (text) on your behalf — costs credits.',
  'entities:web_search': 'Search the live web on your behalf — costs credits.',
  'litcraft:hub':
    'See your LitCraft profile, stats and inventory, and the LitCraft community leaderboards, play time and activity. It can hide your name on leaderboards, but only you can show it again.',
  'navigation:open_arche': 'Open other arches for you (navigation only).',
  'sandbox:vendored-libraries':
    'Load platform-hosted libraries (like the PDF renderer) from the trusted CDN.',
  'schedule:invoke':
    'Schedule an action for later — like a post at 9 AM tomorrow. You approve exactly what will be sent and when, and can see or cancel what it scheduled.',
  'storage:local':
    'Keep its working files on this device, so a refresh or crash does not lose your work. Nothing leaves your device; cleared when you sign out or when someone else signs in here.',
  'storage:sandbox': "Save files in this arche's own private storage.",
  'store:purchase': 'Offer purchases inside the arche (you approve each purchase).',
  'theme:read': 'Match your platform theme.',
  'user:profile:read': 'See your display name and avatar.',
  'vault:arche-saves:write': 'Saves files to your Arche Saves automatically.',
  'vault:documents:read': 'Read files this arche itself created, in its own folders of your Vault.',
  'vault:documents:write': 'Create and manage its own files, in its own folder of your Vault.',
  'vault:user-documents:edit':
    'Save changes to files of yours that you let it edit — you choose each file, earlier versions are kept unless you turned version history off, and you can remove access any time in Vault → Arche Access.',
  'vault:user-documents:read':
    'Read documents YOU choose to share with it from your Vault — you pick each document, and you can remove access any time in Vault → Arche Access.',
  'vault:user-documents:write':
    'Save its work to your Documents — you choose the folder and the name each time.',
  'vault:workspace-documents:read':
    "Read every document you can see in a team workspace — only after that workspace's owner or admin approves it there, and never files marked proprietary.",
};
